// 학생 상세 공용 페이지 — manager/admin/instructor/consultant/viewer 대시보드의
// student/:studentId 라우트에서 사용. 상단 학생 요약(위험 배지·자기주도지수·담당 매니저) +
// 내부 9탭: 이용시간·마인드·일기·학습·과제·피드백·학습진단·진로설계·상담. 과제·피드백·상담
// 탭은 CRUD 포함, viewer는 작성 불가(canWrite=false). 첫 탭은 센터 이용시간+예약 현황
// (클라이언트 요청 2026-08: "명단에서 클릭하면 첫 화면이 센터 이용시간 등록 상황").

import { useState, useMemo, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { User, AlertCircle, Plus, Pencil, Trash2 } from 'lucide-react'
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts'
import { useAuth } from '../../context/AuthContext.jsx'
import { useData } from '../../context/DataContext.jsx'
import { supabase } from '../../lib/supabase.js'
import { toAttendanceSchedule } from '../../lib/supabaseHelpers.js'
import GrowthReportModal from '../../components/reports/GrowthReportModal.jsx'
import StudentScheduleSection from './StudentScheduleSection.jsx'
import {
  buildStudentCounselingEntries, formatScheduleBlocks,
} from '../../context/selectors/studentCounselingReport.js'
import { getMindStatus } from '../../context/selectors/riskDetection.js'
import { COUNSELING_TYPE_LABELS, COUNSELING_TARGET_LABELS } from '../../data/counselingTypes.js'
import CounselingFormModal from '../../components/counseling/CounselingFormModal.jsx'
import CounselingRecordBody from '../../components/counseling/CounselingRecordBody.jsx'
import { AttachmentChips } from '../../components/counseling/AttachmentField.jsx'
import { removeCounselingFiles, filterUnreferencedPaths } from '../../lib/counselingFiles.js'
import { buildCounselingGroupSizes, counselingGroupKey } from '../../context/selectors/counselingGroup.js'
import { educatorDisplayName } from '../../utils/educatorName.js'
import { todayStr } from '../../utils/dateUtils.js'
import ExamKeySelect from '../../components/examReports/ExamKeySelect.jsx'
import ExamFeedbackForm from '../../components/examReports/ExamFeedbackForm.jsx'
import { defaultExamKey, examKeyLabel, sameExamKey } from '../../utils/examTerms.js'
import TaskFormModal from '../../components/tasks/TaskFormModal.jsx'
import TaskFileChips from '../../components/tasks/TaskFileChips.jsx'
import DownloadPdfButton from '../../pdf/components/DownloadPdfButton.jsx'
import { buildFilename } from '../../pdf/utils/formatters.js'
import { STAGE_META, STAGE_ORDER } from '../../data/stageFeedbackLibrary.js'
import { DOMAIN_LABELS } from '../../data/questions.js'
import { actualMinutes, methodBreakdown } from '../../context/selectors/learningRecords.js'
import {
  calcDomainScores, calcStageScores, calcStageGrades,
  getStageStatus, getStateType, buildCoachingReport, selectTasks, getTypeName, scoreToGrade,
} from '../../utils/learningDiagnosisEngine.js'

const RISK_LABELS = {
  normal:  { label: '정상',  color: 'text-green-600 bg-green-100' },
  warning: { label: '주의',  color: 'text-yellow-600 bg-yellow-100' },
  danger:  { label: '위험',  color: 'text-red-600 bg-red-100' },
}

const GRADE_BG = {
  A: 'bg-indigo-100 text-indigo-700',
  B: 'bg-violet-100 text-violet-700',
  C: 'bg-purple-100 text-purple-700',
  D: 'bg-orange-100 text-orange-700',
  E: 'bg-red-100 text-red-700',
}
const GRADE_COLOR = { A: 'text-indigo-600', B: 'text-violet-600', C: 'text-purple-500', D: 'text-orange-500', E: 'text-red-500' }

const CATEGORIES_LABEL = {
  HUMANITIES: '인문', SOCIAL: '사회', ENGINEERING: '공학',
  NATURAL: '자연', HEALTH: '보건', EDUCATION: '교육', ARTS_SPORTS: '예체능',
}

function mindScoreColor(total) {
  if (total > 3) return 'text-blue-600'
  if (total < -3) return 'text-red-600'
  return 'text-gray-600'
}
function mindScoreLabel(n) { return n > 0 ? `+${n}` : String(n) }

// ─── 탭: 마인드 ───────────────────────────────────────────────
function MindSection({ studentId, data, getWeeklyLearning }) {
  const records = data.mindRecords
    .filter((r) => r.studentId === studentId)
    .slice()
    .sort((a, b) => (b.date > a.date ? 1 : -1))

  const weekChart = getWeeklyLearning(studentId)

  if (records.length === 0) {
    return <p className="text-sm text-gray-400 py-8 text-center">마인드 기록이 없습니다.</p>
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl p-4 shadow-sm">
        <h4 className="text-sm font-bold text-gray-700 mb-3">최근 7일 학습시간 (분)</h4>
        {weekChart.some((d) => d.minutes > 0) ? (
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={weekChart} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => [`${v}분`, '학습시간']} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Line type="monotone" dataKey="minutes" stroke="#6366f1" strokeWidth={2.5} dot={{ fill: '#6366f1', r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-xs text-gray-400 text-center py-4">최근 7일 학습 기록 없음</p>
        )}
      </div>

      <div className="space-y-2">
        {records.map((r) => {
          const total = (r.mood ?? 0) + (r.motivation ?? 0) + (r.confidence ?? 0)
          return (
            <div key={r.id} className="bg-white rounded-xl p-3 shadow-sm">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-400">{r.date}</span>
                <span className={`text-sm font-bold ${mindScoreColor(total)}`}>합계 {mindScoreLabel(total)}점</span>
              </div>
              <p className="text-xs text-gray-500">
                기분 {mindScoreLabel(r.mood ?? 0)} · 동기 {mindScoreLabel(r.motivation ?? 0)} · 자신감 {mindScoreLabel(r.confidence ?? 0)}
              </p>
              {r.memo && <p className="text-xs text-gray-500 mt-1 italic">"{r.memo}"</p>}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── 탭: 일기 ─────────────────────────────────────────────────
function DiarySection({ studentId, data }) {
  const records = data.diaryRecords
    .filter((r) => r.studentId === studentId)
    .slice()
    .sort((a, b) => (b.date > a.date ? 1 : -1))

  if (records.length === 0) {
    return <p className="text-sm text-gray-400 py-8 text-center">일기 기록이 없습니다.</p>
  }

  return (
    <div className="space-y-3">
      {records.map((r) => (
        <div key={r.id} className="bg-white rounded-2xl p-4 shadow-sm space-y-2">
          <p className="text-xs text-gray-400 font-semibold">{r.date}</p>
          {r.praise && (
            <div>
              <p className="text-xs font-semibold text-green-600 mb-0.5">칭찬할 점</p>
              <p className="text-sm text-gray-700">{r.praise}</p>
            </div>
          )}
          {r.reflection && (
            <div>
              <p className="text-xs font-semibold text-orange-500 mb-0.5">반성할 점</p>
              <p className="text-sm text-gray-700">{r.reflection}</p>
            </div>
          )}
          {r.resolution && (
            <div>
              <p className="text-xs font-semibold text-blue-600 mb-0.5">내일의 다짐</p>
              <p className="text-sm text-gray-700">{r.resolution}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ─── 탭: 학습 ─────────────────────────────────────────────────
function LearningSection({ studentId, data }) {
  const records = data.learningRecords
    .filter((r) => r.studentId === studentId)
    .slice()
    .sort((a, b) => (b.date > a.date ? 1 : -1))

  const timeRecords = records.filter((r) => actualMinutes(r) > 0)
  const totalMin = timeRecords.reduce((s, r) => s + actualMinutes(r), 0)

  // 과목별 합계
  const bySubject = {}
  timeRecords.forEach((r) => {
    bySubject[r.subject] = (bySubject[r.subject] ?? 0) + actualMinutes(r)
  })
  const subjectData = Object.entries(bySubject)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([name, minutes]) => ({ name, minutes }))
  const methodData = methodBreakdown(timeRecords, 8)

  if (records.length === 0) {
    return <p className="text-sm text-gray-400 py-8 text-center">학습 기록이 없습니다.</p>
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-blue-50 rounded-xl p-3 text-center">
          <p className="text-xl font-bold text-blue-600">{totalMin}</p>
          <p className="text-xs text-gray-500 mt-0.5">총 학습 (분)</p>
        </div>
        <div className="bg-indigo-50 rounded-xl p-3 text-center">
          <p className="text-xl font-bold text-indigo-600">{timeRecords.length}</p>
          <p className="text-xs text-gray-500 mt-0.5">기록 횟수</p>
        </div>
      </div>

      {subjectData.length > 0 && (
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h4 className="text-sm font-bold text-gray-700 mb-3">과목별 학습시간 (분)</h4>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={subjectData} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
              <XAxis type="number" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={60} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => [`${v}분`]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="minutes" fill="#6366f1" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {methodData.length > 0 && (
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h4 className="text-sm font-bold text-gray-700 mb-3">학습법별 학습시간 (분)</h4>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={methodData} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
              <XAxis type="number" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={70} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => [`${v}분`]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="minutes" fill="#10b981" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="space-y-2">
        {records.slice(0, 30).map((r) => (
          <div key={r.id} className="bg-white rounded-xl p-3 shadow-sm flex items-center gap-3">
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-800">{r.subject}</span>
                <span className="text-xs text-gray-400">{r.date}</span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                {actualMinutes(r) > 0 && (
                  <span className="text-xs text-blue-600 font-semibold">{actualMinutes(r)}분</span>
                )}
                <span className="text-xs text-gray-400">{r.recordType === 'planner' ? '계획' : '타이머'}</span>
                {r.studyMethod && <span className="text-xs text-gray-400">{r.studyMethod}</span>}
                {r.studyLocation && <span className="text-xs text-gray-400">{r.studyLocation}</span>}
                {r.focus != null && <span className="text-xs text-gray-400">집중도 {r.focus}%</span>}
              </div>
              {r.content && <p className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">{r.content}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── 탭: 과제 ─────────────────────────────────────────────────
function TaskSection({ studentId, data, currentUser, canWrite = false }) {
  const [showForm, setShowForm] = useState(false)
  const [editTask, setEditTask] = useState(null)
  const { deleteTask } = useData()

  const student = data.students.find((s) => s.id === studentId)
  const tasks = data.tasks
    .filter((t) => t.studentId === studentId)
    .slice()
    .sort((a, b) => (b.dueDate > a.dueDate ? 1 : -1))

  const pending = tasks.filter((t) => t.status === 'pending')
  const done = tasks.filter((t) => t.status === 'done')

  // 본인 부여분 or admin 수정/삭제 가능. assignerId 없는 기존 과제는 admin만.
  const canManage = (t) =>
    canWrite && (t.assignerId === currentUser?.id || currentUser?.role === 'admin')

  const handleDelete = async (id) => {
    if (!window.confirm('이 과제를 삭제할까요?')) return
    try {
      await deleteTask(id)
    } catch {
      // 실패는 전역 Toast가 표면화한다.
    }
  }

  const ManageButtons = ({ t }) =>
    canManage(t) ? (
      <div className="flex items-center gap-2 flex-shrink-0">
        <button onClick={() => setEditTask(t)} className="text-gray-400 hover:text-blue-600 p-0.5" aria-label="과제 수정">
          <Pencil size={14} />
        </button>
        <button onClick={() => handleDelete(t.id)} className="text-gray-400 hover:text-red-600 p-0.5" aria-label="과제 삭제">
          <Trash2 size={14} />
        </button>
      </div>
    ) : null

  return (
    <div className="space-y-4">
      {canWrite && (
        <div className="flex justify-end">
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1 bg-emerald-500 text-white text-sm font-bold px-3 py-2 rounded-xl hover:bg-emerald-600 active:scale-95 transition-all"
          >
            <Plus size={16} /> 과제 추가
          </button>
        </div>
      )}

      {tasks.length === 0 ? (
        <p className="text-sm text-gray-400 py-8 text-center">과제가 없습니다.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-orange-50 rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-orange-600">{pending.length}</p>
              <p className="text-xs text-gray-500 mt-0.5">미완료</p>
            </div>
            <div className="bg-green-50 rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-green-600">{done.length}</p>
              <p className="text-xs text-gray-500 mt-0.5">완료</p>
            </div>
          </div>

          {pending.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-gray-500 font-semibold">미완료 과제</p>
              {pending.map((t) => (
                <div key={t.id} className="bg-white rounded-xl p-3 shadow-sm border-l-4 border-orange-400 flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-gray-800">{t.title}</p>
                    {t.content && (
                      <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap leading-relaxed">{t.content}</p>
                    )}
                    {t.method && (
                      <p className="text-xs text-gray-500 mt-1">수행방법: {t.method}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">
                      {t.subject}{t.assignerName ? ` · 출제: ${t.assignerName}` : ''} · 마감 {t.dueDate}
                    </p>
                    <TaskFileChips attachments={t.attachments} submissions={t.submissions} />
                  </div>
                  <ManageButtons t={t} />
                </div>
              ))}
            </div>
          )}

          {done.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-gray-500 font-semibold">완료된 과제</p>
              {done.map((t) => (
                <div key={t.id} className="bg-gray-50 rounded-xl p-3 opacity-60 flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-gray-500 line-through">{t.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{t.subject}{t.assignerName ? ` · ${t.assignerName}` : ''}</p>
                    <TaskFileChips attachments={t.attachments} submissions={t.submissions} />
                  </div>
                  <ManageButtons t={t} />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showForm && (
        <TaskFormModal fixedStudent={student} onClose={() => setShowForm(false)} />
      )}
      {editTask && (
        <TaskFormModal task={editTask} onClose={() => setEditTask(null)} />
      )}
    </div>
  )
}

// ─── 탭: 피드백 ───────────────────────────────────────────────
// 수시 코멘트 (studentFeedbacks) — 학생 명단 '피드백' 모달과 같은 데이터.
function FeedbackSection({ studentId, data, currentUser, canWrite = false }) {
  const { addStudentFeedback, updateStudentFeedback, deleteStudentFeedback } = useData()
  const [date, setDate] = useState(todayStr())
  const [content, setContent] = useState('')
  const [busy, setBusy] = useState(false)
  const [editId, setEditId] = useState(null) // 인라인 수정 대상 피드백 id
  const [editDate, setEditDate] = useState('')
  const [editContent, setEditContent] = useState('')

  const feedbacks = data.studentFeedbacks
    .filter((f) => f.studentId === studentId)
    .slice()
    .sort((a, b) => (a.date !== b.date
      ? (b.date > a.date ? 1 : -1)
      : ((b.createdAt ?? '') > (a.createdAt ?? '') ? 1 : -1)))

  // 본인 작성분 or admin만 수정/삭제
  const canManage = (f) =>
    canWrite && (f.authorId === currentUser?.id || currentUser?.role === 'admin')

  const handleAdd = async () => {
    if (!content.trim() || busy) return
    setBusy(true)
    try {
      await addStudentFeedback({
        studentId,
        authorId: currentUser?.id ?? null,
        authorName: currentUser?.name ?? '',
        date,
        content: content.trim(),
      })
      setContent('')
      setDate(todayStr())
    } catch {
      alert('피드백 저장 중 오류가 발생했습니다.')
    } finally {
      setBusy(false)
    }
  }

  const handleEditSave = async () => {
    if (!editContent.trim() || busy) return
    setBusy(true)
    try {
      await updateStudentFeedback(editId, { date: editDate, content: editContent.trim() })
      setEditId(null)
    } catch {
      alert('피드백 수정 중 오류가 발생했습니다.')
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('이 피드백을 삭제할까요?')) return
    try {
      await deleteStudentFeedback(id)
    } catch {
      alert('피드백 삭제 중 오류가 발생했습니다.')
    }
  }

  return (
    <div className="space-y-4">
      {canWrite && (
        <div className="bg-white rounded-2xl p-4 shadow-sm space-y-2">
          <h4 className="text-sm font-bold text-gray-700">피드백 작성</h4>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm"
          />
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={3}
            placeholder="기록 내용에 대한 코멘트를 입력하세요"
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm resize-none"
          />
          <div className="flex justify-end">
            <button
              onClick={handleAdd}
              disabled={busy || !content.trim()}
              className="flex items-center gap-1 bg-emerald-500 text-white text-sm font-bold px-3 py-2 rounded-xl hover:bg-emerald-600 active:scale-95 transition-all disabled:opacity-40"
            >
              <Plus size={16} /> {busy ? '저장 중…' : '피드백 저장'}
            </button>
          </div>
        </div>
      )}

      {feedbacks.length === 0 ? (
        <p className="text-sm text-gray-400 py-8 text-center">피드백이 없습니다.</p>
      ) : (
        <div className="space-y-2">
          {feedbacks.map((f) => (
            <div key={f.id} className="bg-white rounded-xl p-3 shadow-sm">
              {editId === f.id ? (
                <div className="space-y-2">
                  <input
                    type="date"
                    value={editDate}
                    onChange={(e) => setEditDate(e.target.value)}
                    className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs"
                  />
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={3}
                    className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs resize-none"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditId(null)}
                      className="flex-1 py-1.5 rounded-lg bg-gray-100 text-xs font-semibold text-gray-600"
                    >
                      취소
                    </button>
                    <button
                      onClick={handleEditSave}
                      disabled={busy || !editContent.trim()}
                      className="flex-1 py-1.5 rounded-lg bg-blue-600 text-xs font-semibold text-white disabled:opacity-40"
                    >
                      수정 저장
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-400">
                      {f.date}{f.authorName ? ` · ${f.authorName}` : ''}
                    </span>
                    {canManage(f) && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setEditId(f.id)
                            setEditDate(f.date)
                            setEditContent(f.content)
                          }}
                          className="text-gray-400 hover:text-blue-600 p-0.5"
                          aria-label="피드백 수정"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(f.id)}
                          className="text-gray-400 hover:text-red-600 p-0.5"
                          aria-label="피드백 삭제"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{f.content}</p>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── 탭: 학습진단 ─────────────────────────────────────────────
function LearningDiagnosisSection({ studentId, data }) {
  const [resultTab, setResultTab] = useState(0)
  const diagResult = data.learningDiagnosisResults?.find((r) => r.studentId === studentId)

  const result = useMemo(() => {
    if (!diagResult?.answers) return null
    const domainScores  = calcDomainScores(diagResult.answers)
    const stageScores   = calcStageScores(domainScores)
    const stageGrades   = calcStageGrades(stageScores)
    const stageStatus   = getStageStatus(stageGrades)
    const stateTypes    = getStateType(stageGrades)
    const coaching      = buildCoachingReport(stageGrades, stageStatus)
    const tasks         = selectTasks(stageGrades)
    const typeName      = getTypeName(stageGrades)
    return { domainScores, stageScores, stageGrades, stageStatus, stateTypes, coaching, tasks, typeName }
  }, [diagResult])

  if (!result) {
    return <p className="text-sm text-gray-400 py-8 text-center">학습 진단 결과가 없습니다.</p>
  }

  const { domainScores, stageScores, stageGrades, stageStatus, stateTypes, coaching, tasks, typeName } = result

  const chartData = STAGE_ORDER.map((s) => ({
    name: STAGE_META[s].label,
    score: stageScores[s],
    color: STAGE_META[s].color,
  }))

  const domainChartData = Object.entries(domainScores).map(([d, score]) => ({
    name: DOMAIN_LABELS[d],
    score,
    grade: scoreToGrade(score),
  }))

  const TABS = ['요약', '코칭 리포트', '실행 과제']

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-indigo-500 to-violet-600 rounded-2xl p-4 text-white">
        <p className="text-xs opacity-75 mb-1">학습 유형</p>
        <h3 className="text-lg font-bold mb-1">{typeName}</h3>
        <div className="flex flex-wrap gap-1.5">
          {stateTypes.map((t) => (
            <span key={t} className="text-xs bg-white/20 rounded-full px-2.5 py-0.5">{t}</span>
          ))}
        </div>
        <p className="text-xs opacity-60 mt-2">학습진단일: {diagResult.date}</p>
      </div>

      <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
        {TABS.map((tab, i) => (
          <button
            key={tab}
            onClick={() => setResultTab(i)}
            className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${
              resultTab === i ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {resultTab === 0 && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <p className="text-xs font-semibold text-gray-500 mb-3">학습 단계 점수</p>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={chartData} margin={{ top: 4, right: 4, bottom: 4, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => [`${v}점`, '점수']} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="score" radius={[6, 6, 0, 0]}>
                  {chartData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-4 gap-2 mt-3">
              {STAGE_ORDER.map((s) => (
                <div key={s} className="text-center">
                  <p className="text-xs text-gray-400">{STAGE_META[s].label}</p>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${GRADE_BG[stageGrades[s]]}`}>
                    {stageGrades[s]}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-indigo-50 rounded-xl p-3">
              <p className="text-xs font-semibold text-indigo-600 mb-2">강점 단계</p>
              {stageStatus.strongStages.map((s) => (
                <div key={s} className="flex items-center gap-1.5 mb-1">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STAGE_META[s].color }} />
                  <span className="text-xs text-gray-700">{STAGE_META[s].label}</span>
                </div>
              ))}
            </div>
            <div className="bg-orange-50 rounded-xl p-3">
              <p className="text-xs font-semibold text-orange-600 mb-2">보완 필요</p>
              {stageStatus.weakStages.length > 0 ? stageStatus.weakStages.map((s) => (
                <div key={s} className="flex items-center gap-1.5 mb-1">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STAGE_META[s].color }} />
                  <span className="text-xs text-gray-700">{STAGE_META[s].label}</span>
                </div>
              )) : <p className="text-xs text-gray-400">없음</p>}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <p className="text-xs font-semibold text-gray-500 mb-3">영역별 점수</p>
            <div className="space-y-2.5">
              {domainChartData.map(({ name, score, grade }) => (
                <div key={name} className="flex items-center gap-2">
                  <span className="text-xs text-gray-600 w-16 flex-shrink-0">{name}</span>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-indigo-400" style={{ width: `${score}%` }} />
                  </div>
                  <span className={`text-xs font-bold w-6 text-right ${GRADE_COLOR[grade]}`}>{grade}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {resultTab === 1 && (
        <div className="space-y-3">
          {[
            { label: '① 현재 상태', text: coaching.currentState, bg: 'bg-slate-50 border-slate-200' },
            { label: '② 강점',      text: coaching.strength,     bg: 'bg-indigo-50 border-indigo-200' },
            { label: '③ 문제 원인', text: coaching.problemCause, bg: 'bg-orange-50 border-orange-200' },
            { label: '④ 개선 방향', text: coaching.improvement,  bg: 'bg-green-50 border-green-200' },
          ].map(({ label, text, bg }) => (
            <div key={label} className={`rounded-xl p-4 border ${bg}`}>
              <p className="text-xs font-bold text-gray-500 mb-1.5">{label}</p>
              <p className="text-sm text-gray-700 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      )}

      {resultTab === 2 && (
        <div className="space-y-3">
          {tasks.map((item, idx) => (
            <div key={idx} className="bg-white rounded-xl p-4 border border-gray-200 flex items-start gap-3">
              <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 mt-0.5 ${GRADE_BG[stageGrades[item.stage]] || 'bg-gray-100 text-gray-500'}`}>
                {item.label}
              </span>
              <span className="text-sm text-gray-800">{item.task}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── 탭: 진로설계 ─────────────────────────────────────────────
function CareerDesignSection({ studentId, data }) {
  const careerResult = data.careerDesignResults?.find((r) => r.studentId === studentId)

  if (!careerResult) {
    return <p className="text-sm text-gray-400 py-8 text-center">진로설계 결과가 없습니다.</p>
  }

  const { typeName, primaryCat, finalScores, fields, date } = careerResult

  const scoreData = finalScores
    ? Object.entries(finalScores)
        .map(([k, v]) => ({ name: CATEGORIES_LABEL[k] ?? k, score: v }))
        .sort((a, b) => b.score - a.score)
    : []

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-4 text-white">
        <p className="text-xs opacity-75 mb-1">진로 유형</p>
        <h3 className="text-lg font-bold">{typeName}</h3>
        {primaryCat && (
          <p className="text-sm opacity-80 mt-1">주요 계열: {CATEGORIES_LABEL[primaryCat] ?? primaryCat}</p>
        )}
        <p className="text-xs opacity-60 mt-2">검사일: {date}</p>
      </div>

      {scoreData.length > 0 && (
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <p className="text-xs font-semibold text-gray-500 mb-3">계열별 점수</p>
          <div className="space-y-2">
            {scoreData.map(({ name, score }) => (
              <div key={name} className="flex items-center gap-2">
                <span className="text-xs text-gray-600 w-12 flex-shrink-0">{name}</span>
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-violet-400 transition-all"
                    style={{ width: `${Math.min(100, Math.round((score / Math.max(...scoreData.map(d => d.score))) * 100))}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500 w-6 text-right">{score}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {fields && fields.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm font-bold text-gray-700">추천 분야</p>
          {fields.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl p-4 shadow-sm">
              <p className="font-semibold text-gray-800 mb-2">{f.field}</p>
              <div className="space-y-1.5">
                <div>
                  <span className="text-xs font-semibold text-violet-600">관련 학과 </span>
                  <span className="text-xs text-gray-600">{f.majors?.join(', ')}</span>
                </div>
                <div>
                  <span className="text-xs font-semibold text-blue-600">직업 </span>
                  <span className="text-xs text-gray-600">{f.jobs?.join(', ')}</span>
                </div>
                <div>
                  <span className="text-xs font-semibold text-green-600">활동 </span>
                  <span className="text-xs text-gray-600">{f.activities?.join(', ')}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── 탭: 상담 ─────────────────────────────────────────────────
function CounselingSection({ studentId, data, currentUser, canWrite = true }) {
  const [showForm, setShowForm] = useState(false)
  const [editRecord, setEditRecord] = useState(null)
  const authorId = currentUser?.id

  const student = data.students.find((s) => s.id === studentId)
  const records = data.counselingRecords
    .filter((r) => r.studentId === studentId)
    .slice()
    .sort((a, b) => (b.date > a.date ? 1 : -1))
  // 그룹 배지용 — 학생 필터 전의 전체 기록 기준
  const groupSizes = useMemo(() => buildCounselingGroupSizes(data.counselingRecords), [data.counselingRecords])

  const { deleteCounselingRecord } = useData()

  const canManage = (r) =>
    canWrite && (r.educatorId === currentUser?.id || currentUser?.role === 'admin')

  const handleDelete = async (record) => {
    if (!window.confirm('이 상담 기록을 삭제할까요?')) return
    try {
      await deleteCounselingRecord(record.id)
      // 실파일 정리 (best-effort) — fan-out 형제 기록이 참조하는 path는 남긴다
      removeCounselingFiles(filterUnreferencedPaths(
        record.attachments?.map((a) => a.path), data.counselingRecords, record.id
      ))
    } catch {
      // 실패는 전역 Toast가 표면화한다.
    }
  }

  const buildCounselingPdf = useCallback(async () => {
    // 등록일정(자동 출결 시간블록) — admin fetch에는 attendance_schedules가 없다
    // → 컨텍스트에 없으면 여기서만 lazy fetch (buildReflectionPdf와 동일 패턴).
    let schedules = data.attendanceSchedules.filter((s) => s.studentId === studentId)
    if (schedules.length === 0) {
      try {
        const res = await supabase
          .from('attendance_schedules')
          .select('*')
          .eq('student_id', studentId)
        if (res.error) throw res.error
        schedules = (res.data ?? []).map(toAttendanceSchedule)
      } catch {
        schedules = [] // 등록일정 없이 출력
      }
    }

    const { entries, totalCount, totalHours, periodText } = buildStudentCounselingEntries(
      records.map((r) => {
        const typeLabel = COUNSELING_TYPE_LABELS[r.type] || r.type
        const targetLabel = COUNSELING_TARGET_LABELS[r.targetType]
        return {
          ...r,
          fallbackContent: r.comment,
          educatorName: educatorDisplayName(data.educators.find((e) => e.id === r.educatorId)) ?? '-',
          // 피상담자가 학생이 아니면 유형에 병기 (구 양식의 '대상' 컬럼 대체)
          typeLabel: r.targetType && r.targetType !== 'student' ? `${typeLabel}(${targetLabel})` : typeLabel,
        }
      }),
    )
    const { default: StudentCounselingReport } = await import('../../pdf/reports/StudentCounselingReport.jsx')
    return {
      element: (
        <StudentCounselingReport
          header={{
            studentName: student?.name,
            schoolGrade: [student?.school, student?.grade].filter(Boolean).join(' '),
            periodText,
            scheduleText: formatScheduleBlocks(schedules),
            totalCount,
            totalHours,
          }}
          entries={entries}
        />
      ),
      filename: buildFilename('상담보고서', student?.name),
    }
  }, [records, student, studentId, data.educators, data.attendanceSchedules])

  // 종합성장리포트 — 월 선택·차트·코칭 편집이 있는 전용 모달로 이동 (2026-08 개편)
  const [showGrowthReport, setShowGrowthReport] = useState(false)

  return (
    <div className="space-y-3">
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => setShowGrowthReport(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 active:scale-95 transition"
        >
          종합성장리포트
        </button>
        <DownloadPdfButton
          buildDocument={buildCounselingPdf}
          label="상담 보고서"
          disabled={records.length === 0}
        />
        {canWrite && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1 bg-emerald-500 text-white text-sm font-bold px-3 py-2 rounded-xl hover:bg-emerald-600 active:scale-95 transition-all"
          >
            <Plus size={16} /> 상담 작성
          </button>
        )}
      </div>

      {records.length === 0 ? (
        <p className="text-sm text-gray-400 py-8 text-center">상담 기록이 없습니다.</p>
      ) : (
        records.map((r) => {
          const author = data.educators.find((e) => e.id === r.educatorId)
          return (
            <div key={r.id} className="bg-white rounded-2xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                    {COUNSELING_TYPE_LABELS[r.type] || r.type}
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                    {COUNSELING_TARGET_LABELS[r.targetType] ?? '학생'}
                  </span>
                  {(groupSizes.get(counselingGroupKey(r)) ?? 0) >= 2 && (
                    <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full">
                      그룹 {groupSizes.get(counselingGroupKey(r))}명
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">{r.date}</span>
                  {canManage(r) && (
                    <>
                      <button
                        onClick={() => setEditRecord(r)}
                        className="text-gray-400 hover:text-blue-600 p-0.5"
                        aria-label="상담 수정"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(r)}
                        className="text-gray-400 hover:text-red-600 p-0.5"
                        aria-label="상담 삭제"
                      >
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <CounselingRecordBody record={r} fallback={r.comment} />
              <AttachmentChips attachments={r.attachments} />
              {author && <p className="text-xs text-gray-400 mt-2">작성: {educatorDisplayName(author)}</p>}
            </div>
          )
        })
      )}

      {showGrowthReport && (
        <GrowthReportModal
          student={student}
          onClose={() => setShowGrowthReport(false)}
        />
      )}

      {showForm && (
        <CounselingFormModal
          fixedStudent={student}
          authorId={authorId}
          onClose={() => setShowForm(false)}
        />
      )}

      {editRecord && (
        <CounselingFormModal
          record={editRecord}
          authorId={authorId}
          onClose={() => setEditRecord(null)}
        />
      )}
    </div>
  )
}

// ─── 내신 피드백 (강사 대리 작성·열람) ───────────────────────
function ExamFeedbackSection({ student, data, canWrite }) {
  const [examKey, setExamKey] = useState(() => defaultExamKey())
  const mine = (data.examFeedbacks ?? []).filter((f) => f.studentId === student.id)
  const existing = mine.find((f) => sameExamKey(f, examKey)) ?? null
  const past = [...mine].sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''))
  return (
    <div className="space-y-4">
      <ExamKeySelect value={examKey} onChange={(k) => k && setExamKey(k)} className="w-full" />
      <ExamFeedbackForm
        key={`${examKey.examYear}-${examKey.semester}-${examKey.examType}`}
        student={student} examKey={examKey} existing={existing} readOnly={!canWrite}
      />
      {past.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-bold text-gray-700 px-1">작성된 보고서</p>
          {past.map((f) => (
            <button key={f.id} type="button" onClick={() => setExamKey({ examYear: f.examYear, semester: f.semester, examType: f.examType })}
              className={`w-full flex items-center justify-between bg-white rounded-xl border px-3 py-2.5 text-sm text-left ${
                sameExamKey(f, examKey) ? 'border-blue-300' : 'border-gray-200'
              }`}>
              <span className="text-gray-800">{examKeyLabel(f)}</span>
              <span className="text-xs text-gray-400">{f.updatedByName || ''} · {(f.updatedAt || '').slice(0, 10)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── 메인 ─────────────────────────────────────────────────────
const TABS = ['이용시간', '마인드', '일기', '학습', '과제', '피드백', '학습진단', '진로설계', '상담', '내신']

export default function StudentDetailPage() {
  const { studentId } = useParams()
  const { currentUser } = useAuth()
  const { data, getWeeklyLearning } = useData()
  const [activeTab, setActiveTab] = useState(0)

  const student = data.students.find((s) => s.id === studentId)
  const risk = RISK_LABELS[student?.riskLevel] || RISK_LABELS.normal
  const managerIds = data.assignments.filter((a) => a.studentId === studentId).map((a) => a.educatorId)
  const managers = data.educators.filter((e) => managerIds.includes(e.id) && e.role === 'manager')
  const hasAlert = getMindStatus(data.mindRecords.filter((r) => r.studentId === studentId)) !== null

  if (!student) {
    return <p className="text-sm text-gray-400 py-16 text-center">학생 정보를 찾을 수 없습니다.</p>
  }

  return (
    <div className="space-y-0">
      {/* 학생 요약 */}
      <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
            <User size={20} className="text-gray-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-gray-900">{student.name}</span>
              <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${risk.color}`}>{risk.label}</span>
              {hasAlert && <AlertCircle size={13} className="text-red-500" />}
            </div>
            <p className="text-xs text-gray-400 truncate">
              {student.school} · {student.grade}
              {managers.length > 0 && ` · 담당: ${managers.map((m) => m.name).join(', ')}`}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-lg font-bold text-blue-600">{student.selfIndex}</p>
            <p className="text-xs text-gray-400">자기주도지수</p>
          </div>
        </div>
      </div>

      {/* 콘텐츠 탭 */}
      <div className="flex gap-1 overflow-x-auto scrollbar-none -mx-4 px-4 mb-4">
        {TABS.map((tab, i) => (
          <button
            key={tab}
            onClick={() => setActiveTab(i)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              activeTab === i
                ? 'bg-blue-500 text-white'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 0 && <StudentScheduleSection student={student} />}
      {activeTab === 1 && <MindSection studentId={studentId} data={data} getWeeklyLearning={getWeeklyLearning} />}
      {activeTab === 2 && <DiarySection studentId={studentId} data={data} />}
      {activeTab === 3 && <LearningSection studentId={studentId} data={data} />}
      {activeTab === 4 && <TaskSection studentId={studentId} data={data} currentUser={currentUser} canWrite={currentUser?.role !== 'viewer'} />}
      {activeTab === 5 && <FeedbackSection studentId={studentId} data={data} currentUser={currentUser} canWrite={currentUser?.role !== 'viewer'} />}
      {activeTab === 6 && <LearningDiagnosisSection studentId={studentId} data={data} />}
      {activeTab === 7 && <CareerDesignSection studentId={studentId} data={data} />}
      {activeTab === 8 && <CounselingSection studentId={studentId} data={data} currentUser={currentUser} canWrite={currentUser?.role !== 'viewer'} />}
      {activeTab === 9 && <ExamFeedbackSection student={student} data={data} canWrite={currentUser?.role !== 'viewer'} />}
    </div>
  )
}
